// src/demo/DemoBar.tsx: the demo's chapter bar (docs/DECISIONS.md, "Demo tour"; the owner's pick, direction A), in
// place of "DEMO · sample data · resets nightly" on every demo page. It guides a builder exploring alone through the
// three parts of src/demo/tour.ts, and never covers the page:
//   the landing, sign-in    [DEMO] Sample data · resets nightly
//   part 1 (a phone)        [DEMO] A Room ⌄ ............ [Next: Add Photos →]   the stop he is on, the next one; the
//                           stop name opens every stop, "See the Welcome Email" and a quiet "Skip Ahead: Your Team"
//   part 1 (a laptop)       [DEMO] (1) As a Buyer · A Room  (2) Your Team  (3) Your Business ... (i) [Next: …]
//   parts 2 and 3           the same, "Team ⌄" on a phone; Next: Your Business, then The Pipeline, then Other People
// "Next: Your Team" on a phone first says the team side is best on a laptop ("Continue Here" goes on anyway).
// The bar also runs the tour: it notes each stop he reaches, holds the install card back until the app stop, and at
// the app stop asks for the card (or, on a computer, opens /install, which says to use a phone).
import { useEffect, useRef, useState } from "preact/hooks";
import { Link, navigate, route } from "../app/router";
import { installHeld, raiseInstallTrigger, useInstallPrompt } from "../pwa/prompt";
import { me, site } from "../state/session";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { isPhone } from "../ui/media";
import { toast } from "../ui/Toasts";
import { InfoTip } from "../ui/Tooltip";
import { reach } from "../try/trial";
import { PartIntro } from "../try/PartIntro";
import { DemoPeek } from "./DemoMail";
import { barModel, chapterError, CHAPTERS, goToChapter, holdsInstall, noteStop, stopHref, stopIndex, stopOf, STOPS, tour, tourRoom, tourStops, type NextStep } from "./tour";
import "../styles/demo-bar.css";

const CAVEAT = "Sample data. Email is captured here, never sent. Resets every night.";
/** The public test ride's caveat (docs/DECISIONS.md "Public demo"): the visitor's own copy, for a day. */
export const TRY_CAVEAT = "Your own copy of a made-up builder. Email stays here, never sent. Deleted after 24 hours.";

function caveat(): string {
  return site.value?.trial ? TRY_CAVEAT : CAVEAT;
}

/** The bar's tag: "Demo", or on the test ride "Test Ride". */
function tagText(): string {
  return site.value?.trial ? "Test Ride" : "Demo";
}

/** The bar's short note: "Sample data · resets nightly", or on the test ride "Your copy · 24 hours". */
function shortNote(): string {
  return site.value?.trial ? "Your own copy · 24 hours" : "Sample data · resets nightly";
}

export function DemoBar() {
  if (!site.value?.demo) return null;
  return (
    <>
      <Bar />
      <PartIntro />
    </>
  );
}

type Menu = null | "stops" | "parts" | "hand";

function Bar() {
  const r = route.value;
  const m = tour.value;
  const who = me.value;
  const staff = !!who?.staff;
  const household = !!who?.user && !staff;
  const model = barModel(r, m, tourRoom.value, staff);
  const install = useInstallPrompt();
  const [menu, setMenu] = useState<Menu>(null);
  const [busy, setBusy] = useState(false);
  const pid = r.params.pid ?? null;
  const here = model.chapter === 1 ? stopOf(r) : null;

  // The stop he is on counts as reached (the form for anyone; a board's stops for its family).
  useEffect(() => {
    if (!here) return;
    if (here === "form") noteStop("form", null);
    else if (household && pid) noteStop(here, pid);
    // The test ride: what the visitor reached, for the owner's summary (src/try/trial.ts).
    if (here === "form" || household) reach(`stop:${here}`);
  }, [here, household, pid]);
  useEffect(() => {
    if (model.chapter !== 0) reach(`chapter:${model.chapter}`);
  }, [model.chapter]);

  // Until the app stop, the install card waits on his board (the welcome sheet's own card included); checked again on
  // every page, so a tab left open into the next day lets go.
  useEffect(() => {
    installHeld.value = holdsInstall(m, Date.now(), pid);
  }, [m, pid, r.path]);

  // The app stop: ask for the card; a computer gets /install instead, which says to open it on a phone.
  const asked = useRef<string | null>(null);
  useEffect(() => {
    if (here !== "app" || r.name !== "home" || !household) {
      asked.current = null;
      return;
    }
    // Not before the install prompt knows this browser (right after a reload it is still starting).
    if (!install.available || asked.current === r.path) return;
    asked.current = r.path;
    if (install.platform === "desktop") navigate("/install", { replace: true });
    else raiseInstallTrigger("demo_tour");
  }, [here, r.name, r.path, household, install.available, install.platform]);

  // The menus: focus moves in, Escape closes the menu alone (never a sheet under it), and focus goes back.
  const bar = useRef<HTMLElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  useEffect(() => setMenu(null), [r.path]);
  useEffect(() => {
    if (!menu) return;
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const first = bar.current?.querySelector<HTMLElement>(".db-pop a, .db-pop button");
    first?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopImmediatePropagation();
      e.preventDefault();
      setMenu(null);
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      const back = opener.current;
      if (back && document.contains(back)) back.focus({ preventScroll: true });
    };
  }, [menu]);

  const go = (chapter: 2 | 3, confirmed = false) => {
    if (chapter === 2 && !confirmed && isPhone.peek()) {
      setMenu("hand");
      return;
    }
    setBusy(true);
    goToChapter(chapter).catch((e: unknown) => {
      setBusy(false);
      toast(chapterError(e));
    });
  };
  const close = () => setMenu(null);
  const toggle = (which: Exclude<Menu, null>) => setMenu(menu === which ? null : which);

  if (model.chapter === 0) {
    return (
      <nav class="hb-demo-bar" aria-label="Demo" data-chapter="0">
        <div class="db-in">
          <Link class="db-home" to="/demo" aria-label="Demo home">
            <span class="db-tag">{tagText()}</span>
          </Link>
          <span class="db-note">{shortNote()}</span>
        </div>
      </nav>
    );
  }

  const chapter = model.chapter;
  const boardHome = household && pid ? `/p/${pid}` : null;
  const partsList = <Parts chapter={chapter} sub={model.chapter === 1 ? STOPS[stopIndex(model.stop)]!.name : null} boardHome={boardHome} onPart={(n) => go(n)} />;
  const right = (
    <span class="db-right">
      <InfoTip label="About the demo" align="end">
        {caveat()}
      </InfoTip>
      <Next next={model.next} busy={busy} expanded={menu === "hand"} onChapter={(n) => go(n)} />
    </span>
  );
  const pops = menu ? (
    <>
      <div class="db-pop-scrim" onClick={close} aria-hidden="true" />
      {menu === "hand" ? (
        <div class="db-pop db-hand" role="dialog" aria-label="Best on a laptop">
          <p class="db-hand-h">
            <Icon name="laptop" />
            Best on a Laptop
          </p>
          <p class="db-hand-s">Open the same link there.</p>
          <div class="db-hand-a">
            <Button variant="secondary" busy={busy} onClick={() => go(2, true)}>
              Continue Here
            </Button>
          </div>
        </div>
      ) : menu === "stops" && model.chapter === 1 ? (
        <StopMenu stop={model.stop} reached={model.reached} pid={household ? pid : null} onClose={close} onSkip={() => go(2)} />
      ) : (
        <PartsMenu chapter={chapter} onPart={(n) => (n === 1 ? navigate("/demo") : go(n))} />
      )}
    </>
  ) : null;

  if (model.chapter === 1) {
    const i = stopIndex(model.stop);
    const cur = STOPS[i]!;
    const total = tourStops().length;
    const fill = `${(((Math.min(model.reached, total - 1) + 1) / total) * 100).toFixed(2)}%`;
    return (
      <nav ref={bar} class="hb-demo-bar" aria-label={`Demo, part 1: ${cur.name}, stop ${i + 1} of ${total}`} data-chapter="1" data-tour={cur.id} style={{ "--tour": fill }}>
        <div class="db-in">
          <Link class="db-home" to="/demo" aria-label="Demo home">
            <span class="db-tag">{tagText()}</span>
          </Link>
          {partsList}
          <button
            type="button"
            class="db-now db-stop"
            aria-haspopup="dialog"
            aria-expanded={menu === "stops" ? "true" : "false"}
            aria-label={`${cur.name}, stop ${i + 1} of ${total}. All stops`}
            onClick={() => toggle("stops")}
          >
            {cur.short ? (
              <>
                <span class="st-l">{cur.name}</span>
                <span class="st-s">{cur.short}</span>
              </>
            ) : (
              <span>{cur.name}</span>
            )}
            <Icon name="chev-d" />
          </button>
          {right}
        </div>
        {pops}
      </nav>
    );
  }

  const part = CHAPTERS[chapter - 1]!;
  return (
    <nav ref={bar} class="hb-demo-bar" aria-label={`Demo, part ${chapter} of 3`} data-chapter={String(chapter)}>
      <div class="db-in">
        <Link class="db-home" to="/demo" aria-label="Demo home">
          <span class="db-tag">{tagText()}</span>
        </Link>
        {partsList}
        <button
          type="button"
          class="db-now"
          aria-haspopup="dialog"
          aria-expanded={menu === "parts" ? "true" : "false"}
          aria-label={`Part ${chapter} of 3, ${part.long}. All parts`}
          onClick={() => toggle("parts")}
        >
          <span class="db-seg" aria-hidden="true">
            {[1, 2, 3].map((n) => (
              <i key={n} class={n <= chapter ? "on" : undefined} />
            ))}
          </span>
          <span>{part.short}</span>
          <Icon name="chev-d" />
        </button>
        {right}
      </div>
      {pops}
    </nav>
  );
}

/** The three parts in a row (tablet and desktop). Part 1 leads back to his board, or to the landing for the team. */
function Parts(props: { chapter: 1 | 2 | 3; sub: string | null; boardHome: string | null; onPart: (n: 2 | 3) => void }) {
  return (
    <ol class="db-ch">
      {CHAPTERS.map((c) => {
        const state = c.n < props.chapter ? "done" : c.n === props.chapter ? "current" : "todo";
        const label = (
          <>
            <span class="db-n">{state === "done" ? <Icon name="check" size="xs" /> : c.n}</span>
            <span class="db-l">
              {c.long}
              {c.n === props.chapter && props.sub ? <span class="db-sub">{props.sub}</span> : null}
            </span>
          </>
        );
        return (
          <li key={c.n} class="db-c" data-state={state}>
            {state === "current" ? (
              <span aria-current="step">{label}</span>
            ) : c.n === 1 ? (
              <Link to={props.boardHome ?? "/demo"}>{label}</Link>
            ) : (
              <button type="button" onClick={() => props.onPart(c.n as 2 | 3)}>
                {label}
              </button>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/** The bar's one action. The form has none: Send is the way on, so the bar only says what comes next. */
function Next(props: { next: NextStep; busy: boolean; expanded: boolean; onChapter: (n: 2 | 3) => void }) {
  const n = props.next;
  if (n.kind === "quiet") {
    return (
      <span class="db-next is-quiet" title={n.hint}>
        <span class="k">Next:</span> {n.label}
      </span>
    );
  }
  const long = n.label;
  const short = n.kind === "stop" ? n.label : n.short;
  const inner = (
    <>
      <span class="k">Next:</span> <span class="db-long">{long}</span>
      <span class="db-short">{short}</span>
      <Icon name="arrow-r" />
    </>
  );
  if (n.kind === "chapter") {
    return (
      <button
        type="button"
        class="db-next"
        aria-busy={props.busy ? "true" : undefined}
        aria-expanded={n.chapter === 2 ? (props.expanded ? "true" : "false") : undefined}
        disabled={props.busy}
        onClick={() => props.onChapter(n.chapter)}
      >
        {inner}
      </button>
    );
  }
  return (
    <Link class="db-next" to={n.href}>
      {inner}
    </Link>
  );
}

/** Part 1's stops (the stop name tapped), the welcome email, and a quiet way ahead. */
function StopMenu(props: { stop: (typeof STOPS)[number]["id"]; reached: number; pid: string | null; onClose: () => void; onSkip: () => void }) {
  const cur = stopIndex(props.stop);
  return (
    <div class="db-pop db-menu" role="dialog" aria-label="Part 1, every stop">
      <p class="db-menu-k">Part 1 · As a Buyer</p>
      <ol>
        {tourStops().map((s, i) => {
          const state = i === cur ? "current" : i <= props.reached ? "done" : "todo";
          const row = (
            <>
              <span class="db-n">{state === "done" ? <Icon name="check" size="xs" /> : i + 1}</span>
              <span>
                <b>{s.name}</b>
                <small>{s.hint}</small>
              </span>
            </>
          );
          return (
            <li key={s.id} data-state={state}>
              {props.pid && s.id !== "form" ? (
                <Link to={stopHref(s.id, props.pid, tourRoom.value)} onClick={props.onClose} aria-current={state === "current" ? "step" : undefined}>
                  {row}
                </Link>
              ) : (
                <span class="db-row">{row}</span>
              )}
            </li>
          );
        })}
      </ol>
      {props.pid && props.reached >= 1 ? <DemoPeek pid={props.pid} onOpen={props.onClose} /> : null}
      <div class="db-menu-f">
        <button type="button" class="db-skip" onClick={props.onSkip}>
          Skip Ahead: Your Team
        </button>
        <Link to="/demo">Demo Home</Link>
      </div>
    </div>
  );
}

/** The three parts (a phone, the part tapped). */
function PartsMenu(props: { chapter: 1 | 2 | 3; onPart: (n: 1 | 2 | 3) => void }) {
  return (
    <div class="db-pop db-menu" role="dialog" aria-label="The demo's three parts">
      <ol>
        {CHAPTERS.map((c) => {
          const state = c.n < props.chapter ? "done" : c.n === props.chapter ? "current" : "todo";
          return (
            <li key={c.n} data-state={state}>
              <button type="button" onClick={() => props.onPart(c.n)}>
                <span class="db-n">{state === "done" ? <Icon name="check" size="xs" /> : c.n}</span>
                <span>
                  <b>{c.long}</b>
                  <small>{c.line}</small>
                </span>
                <span class="dev" title={c.device === "mobile" ? "Best on your phone" : "Best on a laptop"}>
                  <Icon name={c.device} />
                </span>
              </button>
            </li>
          );
        })}
      </ol>
      <div class="db-menu-f">
        <span>{shortNote()}</span>
        <Link to="/demo">Demo Home</Link>
      </div>
    </div>
  );
}
